/**
 * Storybooks API Client
 *
 * Calls the Render storybooks service via VITE_STORYBOOKS_API_URL.
 */

import { sbFromEnv } from './supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY!;

const STORYBOOKS_API_URL = import.meta.env.VITE_STORYBOOKS_API_URL || '';

const supabase = sbFromEnv({
  url: supabaseUrl,
  key: supabaseAnon,
});

interface StorybooksApiOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown>;
}

/**
 * Make a request to the storybooks API
 */
export async function storybooksApi<T = unknown>(
  path: string,
  options: StorybooksApiOptions = {}
): Promise<T> {
  // Get fresh session token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const url = `${STORYBOOKS_API_URL}${path}`;

  const res = await fetch(url, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }

  if (!res.ok) {
    const errorBody = body as { error?: { message?: string } | string } | null;
    let msg = `HTTP ${res.status}`;
    if (errorBody?.error) {
      msg = typeof errorBody.error === 'string'
        ? errorBody.error
        : errorBody.error.message || msg;
    }
    throw new Error(msg);
  }

  return body as T;
}

// ============ Job APIs ============

export interface JobProgress {
  meta: {
    total: number;
    completed: number;
    failed: number;
    processed: number;
    style_registry_id?: string | null;
    last_error?: string;
  };
  pages: Array<{
    page_no: number;
    status: 'queued' | 'running' | 'ready' | 'error';
    key: string | null;
    error?: string;
    version: number;
  }>;
}

export interface Job {
  id: string;
  book_id: string;
  status: string;
  total_pages: number;
  completed_pages: number;
  failed_pages: number;
  progress: JobProgress | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Create a new illustration job
 */
export async function createIllustrationJob(params: {
  book_id: string;
  classroom_id?: string;
  style_registry_id?: string;
  pages?: Array<{ page_no: number; text: string }>;
}): Promise<{ data: { job: Job }; error: null }> {
  return storybooksApi('/api/jobs', {
    method: 'POST',
    body: params,
  });
}

/**
 * Get job status
 */
export async function getJobStatus(params: {
  job_id?: string;
  book_id?: string;
  latest?: boolean;
}): Promise<{ data: { job: Job | null }; error: null }> {
  const query = new URLSearchParams();
  if (params.job_id) query.set('job_id', params.job_id);
  if (params.book_id) query.set('book_id', params.book_id);
  if (params.latest) query.set('latest', '1');

  return storybooksApi(`/api/jobs/status?${query.toString()}`);
}

/**
 * Get job by ID
 */
export async function getJob(jobId: string): Promise<{ data: Job; error: null }> {
  return storybooksApi(`/api/jobs/${jobId}`);
}

// ============ Book APIs ============

export interface StoryBook {
  id: string;
  title: string;
  classroom_id: string | null;
  style_registry_id: string | null;
  published: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get storybook by ID
 */
export async function getBook(bookId: string): Promise<{ data: StoryBook; error: null }> {
  return storybooksApi(`/api/books/${bookId}`);
}

// ============ Character APIs ============

export interface StoryCharacter {
  id: string;
  classroom_id: string;
  name: string;
  descriptor: Record<string, unknown>;
  ref_images: string[];
  seed?: string | null;
}

/**
 * Upsert characters for a classroom
 */
export async function upsertCharacters(params: {
  classroom_id: string;
  characters: Array<{
    participant_id?: string | null;
    name: string;
    descriptor?: Record<string, unknown>;
    ref_images?: string[];
  }>;
}): Promise<{ data: { characters: StoryCharacter[] }; error: null }> {
  return storybooksApi('/api/characters/upsert', {
    method: 'POST',
    body: params,
  });
}

// ============ Storage APIs ============

/**
 * Get signed URL for a private storage object
 */
export async function getSignedImageUrl(
  path: string,
  expires: number = 600
): Promise<{ data: { signed_url: string }; error: null }> {
  const query = new URLSearchParams({ path, expires: String(expires) });

  return storybooksApi(`/api/storage/signed-url?${query.toString()}`);
}

// ============ V2 Flow APIs ============

export interface V2InitializeParams {
  book_id: string;
  classroom_id: string;
  page_count?: number;
  style_registry_id?: string;
  student_profiles?: Array<{
    id?: string;
    full_name?: string;
    name?: string;
    nickname?: string;
    role?: string;
    traits?: string | string[];
    grade?: string;
  }>;
  teacher_notes?: string;
}

export interface V2InitializeResult {
  book_id: string;
  job_id: string;
  page_count: number;
  characters: Array<{ id: string; name: string }>;
  scenes: Array<{ page_no: number; arc_position: string; scene_description: string }>;
  message: string;
}

/**
 * Initialize V2 flow - builds characters and queues illustrations
 */
export async function v2Initialize(params: V2InitializeParams): Promise<{ data: V2InitializeResult; error: null }> {
  return storybooksApi('/api/v2/initialize', {
    method: 'POST',
    body: params,
  });
}

export interface V2GenerateNarrativeParams {
  book_id: string;
  teacher_notes?: string;
}

export interface V2NarrativeResult {
  book_id: string;
  narrative: string;
  pages: Array<{ page_no: number; narrative: string }>;
}

/**
 * Generate narrative text after illustrations are complete
 */
export async function v2GenerateNarrative(params: V2GenerateNarrativeParams): Promise<{ data: V2NarrativeResult; error: null }> {
  return storybooksApi('/api/v2/generate-narrative', {
    method: 'POST',
    body: params,
  });
}

// ============ Compat APIs (migrated from Netlify proxy) ============

/**
 * Generate narrative text for a storybook
 */
export async function generateText(params: {
  book_id: string;
  teacher_notes?: string;
  page_count?: number;
  student_profiles?: unknown[];
}): Promise<{ book?: { narrative?: string; [key: string]: unknown } }> {
  return storybooksApi('/api/storybooks/generate-text', {
    method: 'POST',
    body: params,
  });
}

/**
 * Render PDF for a storybook
 */
export async function renderPdf(params: {
  book_id: string;
}): Promise<{ url?: string; signedUrl?: string; pdf_url?: string; pdfUrl?: string }> {
  return storybooksApi('/api/storybooks/render-pdf', {
    method: 'POST',
    body: params,
  });
}

/**
 * Update storybook settings
 */
export async function updateSettings(params: {
  book_id: string;
  [key: string]: unknown;
}): Promise<unknown> {
  return storybooksApi('/api/storybooks/update-settings', {
    method: 'POST',
    body: params,
  });
}

/**
 * Generate illustration for a single page
 */
export async function generateIllustration(params: {
  book_id: string;
  classroom_id?: string;
  page_no: number;
  page_id?: string | null;
  text?: string;
  [key: string]: unknown;
}): Promise<unknown> {
  return storybooksApi('/api/storybooks/generate-illustration', {
    method: 'POST',
    body: params,
  });
}

/**
 * Publish a storybook
 */
export async function publishStorybook(params: {
  book_id: string;
  storybookId?: string;
  consent?: unknown;
}): Promise<{ published?: boolean; url?: string; [key: string]: unknown }> {
  return storybooksApi('/api/storybooks/publish-storybook', {
    method: 'POST',
    body: params,
  });
}

// ============ Utility ============

/**
 * Check if using Render storybooks service
 */
export function isUsingRenderService(): boolean {
  return Boolean(STORYBOOKS_API_URL);
}

/**
 * Get the current storybooks API base URL
 */
export function getStorybooksApiUrl(): string {
  return STORYBOOKS_API_URL || window.location.origin;
}
